// Path helpers for the public marketing + legal pages. English lives at the
// root (/, /pricing), every other language under a bare lowercase slug (/de,
// /de/pricing). URLs never carry a country — /en/* and every lang-country
// combination 301 to their language page (config/routes.rb), and region
// overlays (locales/regions/) apply invisibly at render time from
// CF-IPCountry. The active $locale can therefore be a region code ("de-CH")
// while every URL built from it stays language-only. The authenticated app is
// NOT prefixed and does not use these.

// The supported language codes, straight from the base dictionary filenames
// (mirrors Locales::NAMES). Glob keys are known synchronously — nothing is
// fetched here.
export const LANGUAGES = Object.keys(
  import.meta.glob("../locales/*.json"),
).flatMap((path) => path.match(/([^/]+)\.json$/)?.[1] ?? [])

const SLUG_RE = new RegExp(`^/(?:${LANGUAGES.join("|")})(?=/|$)`)

/** Base language of a public code — strips the region suffix when the
 *  active $locale is a region code ("de-CH" -> "de"). */
export function baseLanguage(code: string): string {
  return code.split("-")[0]
}

/** URL segment for a public code: its base language ("de-CH" -> "de"), since
 *  URLs never carry a country, and nothing for English. Every locale given to
 *  a generated route helper goes through this: they drop an undefined one
 *  (contactPath({ locale: undefined }) -> "/contact") but print "en". */
export function languageSlug(code: string): string | undefined {
  const slug = baseLanguage(code).toLowerCase()
  return slug === "en" ? undefined : slug
}

/** ISO country of a region code ("de-CH" -> "CH"), or null on a bare
 *  language (no overlay active). */
export function regionCountry(code: string): string | null {
  return code.split("-")[1] ?? null
}

/** Homepage URL for a public code: "/" for English, otherwise "/de". */
export function homePath(code: string): string {
  return localized("/", code)
}

/** A base (unprefixed) path such as "/legal" in the code's language. */
export function localized(path: string, code: string): string {
  const slug = languageSlug(code)
  if (!slug) return path || "/"
  return !path || path === "/" ? `/${slug}` : `/${slug}${path}`
}

/** The current location in a different public code (the footer language
 *  select). Strips the current slug prefix first, then re-applies. */
export function switchLocalePath(loc: Location, next: string): string {
  const stripped = loc.pathname.replace(SLUG_RE, "") || "/"
  return localized(stripped, next) + loc.search + loc.hash
}

export interface AlternateLink {
  hreflang: string
  href: string
}

/** hreflang alternates (+ x-default → the English page) for the current page:
 *  one per language, on every page — mirrors SeoController. Region overlays
 *  never get URLs, so no country code appears in the cluster. */
export function hreflangLinks(
  origin: string,
  pathname: string,
): AlternateLink[] {
  const base = pathname.replace(SLUG_RE, "") || "/"
  const links: AlternateLink[] = LANGUAGES.map((code) => ({
    hreflang: code,
    href: origin + localized(base, code),
  }))
  links.push({ hreflang: "x-default", href: origin + localized(base, "en") })
  return links
}
